import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {IFloorplanSection, ILayoutFullTables, ISectionState} from './types';
import DateUtilsService from 'external-services/date-utils-service';
import {
  IFloorplanStructure,
  ILayout,
  ILayoutMinimalAbstract,
  IObjectMap,
  ITableItem,
} from 'shared-types/index';
import {ReduxSlices} from "floorplan-v2/app/appRoot/types";
import SectionLookupService from 'external-services/section-lookup-service/sectionLookup.service';
import LayoutsService from 'external-services/layouts-service/layouts.service';
import TablesLookupService from 'external-services/tables-lookup-service/tablesLookup.service';
import { cloneDeep } from 'lodash-es';
import { getLayoutBySectionLookup } from 'floorplan-v2/app/reducers/sections/helper';
import { FloorplanV2State } from 'floorplan-v2/app/appRoot/rootReducer';
import PouchLoadService from 'external-services/pouch-load-service';

const NS = 'SectionsSlice';

const initialState: ISectionState = {
  floorplanSections: [],
  selectedSectionIds: [],
  showNoSectionBanner: false,
  structures: [],
};
const loadStructure = async (): Promise<{items: IFloorplanStructure[]}> => {
  let result: { items: IFloorplanStructure[] } = { items: [] };
  try {
    const db = PouchLoadService.getDbLocal();
    result = (await db.get('structure')) as unknown as { items: IFloorplanStructure[] };
  } catch (error) {
    console.error('Error loading structure', error);
  }

  return result;
}
export const updateFloorplanSections = createAsyncThunk(
  'floorplanSections/updateFloorplanSections',
  async (payload: { ids: string[]; isSection: boolean }, { dispatch, getState }): Promise<any> => {
    const {
      floorplanSectionsReducer: { floorplanSections },
    } = getState() as FloorplanV2State;
    if (!floorplanSections.length) {
      // if empty forces a refresh
      dispatch(loadFloorplanSections());
      return;
    }
    let refresh: boolean;
    if (payload.isSection) {
      refresh = floorplanSections.some((section) => payload.ids.includes(section._id));
    } else {
      refresh = floorplanSections.some((section) => {
        return section.layouts.some((layout) => payload.ids.includes(layout._id));
      });
    }
    if (refresh) {
      dispatch(loadFloorplanSections());
    }
  }
);

export const loadFloorplanSections = createAsyncThunk(
  'floorplanSections/loadFloorplanSections',
  async (payload: void, { dispatch, getState }): Promise<{
    floorplanSections: IFloorplanSection[], structures: IFloorplanStructure[]
  }> => {
    const {
      scheduleReducer: { selectedDate, selectedSchedule },
      venueReducer: { timeZoneId },
    } = getState() as FloorplanV2State;

    if (selectedDate && selectedSchedule && timeZoneId) {
      const currentTime = DateUtilsService.getVenueTime(timeZoneId);
      // const currentTime = new Date('2024-04-19 08:25:00');
      const sectionIdToLayoutIds = getLayoutBySectionLookup(selectedSchedule.services, currentTime);
      const floorplanSectionsPromise = Promise.all(
        Object.keys(sectionIdToLayoutIds).map(async (sectionId) => {
          const sectionPromise = SectionLookupService.getSectionById(sectionId);
          const layoutsPromise = LayoutsService.getLayouts(
            sectionIdToLayoutIds[sectionId],
            selectedDate
          );
          const [section, layouts] = await Promise.all([sectionPromise, layoutsPromise]);
          /**
           * Layouts from cache. Gets cloned to avoid pollute the cache and object mutations.
           * Forced type to ILayoutFullTables because 'tables' property gets replaced with objects further down.
           */
          const clonedLayouts: ILayoutFullTables[] = cloneDeep(layouts) as unknown as ILayoutFullTables[];

          await Promise.all(
            clonedLayouts.map(async (layout) => {
              if (
                layout.tables &&
                layout.tables.length > 0 &&
                typeof layout.tables[0] === 'string'
              ) {
                layout.tables = await TablesLookupService.getAllTablesByIds(
                  layout.tables as any as string[]
                );
              }
            })
          );
          return {
            _id: section._id,
            name: section.name,
            position: section.position,
            order: section.order,
            layouts: clonedLayouts
          };
        })
      );
      const structuresPromise = loadStructure();
      const [floorplanSections, structures] = await Promise.all([floorplanSectionsPromise, structuresPromise]);
      return { floorplanSections, structures: structures.items };
    }

    return { floorplanSections: [], structures: [] };
  }
);
const sectionsSlice = createSlice({
  name: ReduxSlices.Sections,
  initialState,
  reducers: {
    setFloorplanSections: (state, action: PayloadAction<IFloorplanSection[]>) => {
      state.floorplanSections = action.payload;
    },
    setSelectedSectionIds: (state, action: PayloadAction<string[]>) => {
      state.selectedSectionIds = action.payload;
    },
    setShowNoSectionBanner: (state, action: PayloadAction<boolean>) => {
      state.showNoSectionBanner = action.payload;
    },
    resetFloorplanSections: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadFloorplanSections.fulfilled, (state, action) => {

      state.floorplanSections = action.payload.floorplanSections;
      state.structures = action.payload.structures;
    });
  },
});

const actions = sectionsSlice.actions;
export { actions as floorplanV2SectionsActions };
export const selectFloorplanSections = (state: { sections: ISectionState }) =>
  state.sections.floorplanSections;

export default sectionsSlice.reducer;
